import { clone, isNil } from "ramda";
import axios from "axios";
import { toast } from "react-toastify";
import querystring from "querystring";
import { UNAUTHORIZED_USER } from "../utils/consts";
import {
  authFunction,
  getFromCookie,
  isOldPasswordValid,
  isTokenExpired,
  logout,
  setAuthToken,
  userWantToBeRemember,
} from "./auth";
import { createCookie, eraseCookie } from "../cookie";

axios.defaults.baseURL = window.RUNTIME_CONFIG.BASE_API_URL;

export const login = async (username, password, remember_me, intl, selectedService, access) => {
  try {
    if (!isNil(username) && !isNil(password)) {
      const result = await authFunction(axios, "login", { username, password, remember_me }, intl);
      eraseCookie("uptechRememberMe");
      if (result && remember_me) {
        createCookie("uptechRememberMe", true, 30);
      }
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
  return false;
};

export const getServices = async (intl) => {
  try {
    const result = await axios.get("/default-config");
    return result;
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const register = async (username, password, remember) => {
  try {
    if (!isNil(username) && !isNil(password)) {
      const result = await authFunction(axios, "register", { username, password });
      eraseCookie("uptechRememberMe");
      if (result && remember) {
        createCookie("uptechRememberMe", true, 30);
      }
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
  return false;
};

export const refreshTokenData = async () => {
  try {
    const refreshToken = getFromCookie("uptechRefreshToken");
    if (!isNil(refreshToken)) {
      const result = await authFunction(axios, "refreshToken", refreshToken);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
  return false;
};

export const verifyOldPassword = async (oldPassword, intl) => {
  try {
    if (await preRequest()) {
      const result = await isOldPasswordValid(axios, oldPassword, intl);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

const retryGet = async (url, retries = 3, delay = 1000) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await axios.get(url);
    } catch (error) {
      const isTimeout = error.code === "ECONNABORTED" || error.message?.includes("timeout");

      if (!isTimeout || attempt === retries) throw error;

      await new Promise((res) => setTimeout(res, delay * attempt)); // backoff exponencial
    }
  }
};

export const getUserRoleData = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/roles");
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const postRole = async (role, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.post("/roles", role);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const putRole = async (id, role, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.put(`/roles/${id}`, role);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const deleteRole = async (id, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.delete(`/roles/${id}`);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getMenuData = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/menuOptions");
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const getEquipmentTypes = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/equipmentTypes");
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getAssetsValues = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/data");
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getAssetsValuesByAssets = async (assets) => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/data?asset=${assets}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getEquipments = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/equipments");
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getAllDocuments = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/documents");
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getDocuments = async (asset) => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/documents", { params: { asset: [asset] } });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getDocumentsFile = async (document) => {
  try {
    if (await preRequest()) {
      const { asset, filename, equipmentID } = document;
      const result = await axios.get("/documents/file", {
        params: { asset, filename, equipment_ID: equipmentID },
        responseType: "blob",
      });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const postDocuments = async (document, newName, file, intl) => {
  try {
    if (await preRequest()) {
      const formData = new FormData();
      document.name = newName;
      formData.append("document", JSON.stringify(document));
      formData.append("file", file);
      const result = await axios.post("/documents", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const putDocument = async (document, newName, file, intl) => {
  try {
    if (await preRequest()) {
      const formData = new FormData();
      let newDocument = clone(document);
      newDocument.name = newName;
      formData.append("document", JSON.stringify(newDocument));
      if (!isNil(file)) {
        formData.append("file", file);
      }
      const result = await axios.put(`/documents/${document.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const deleteDocument = async (document, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.delete(`/documents/${document.id}`);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getPoints = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/points");
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getPointsByAssets = async (assets) => {
  try {
    if (await preRequest()) {
      const result = await retryGet(`/points?assets=${assets}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getAssetsData = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/assets");
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const postAsset = async (data, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.post("/assets", data);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const postEquipment = async (data, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.post("/equipments", data);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getEquipmentsByAsset = async (assets) => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/equipments?assets=${assets}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getReports = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/reports");
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getReport = async (id) => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/reports/${id}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const postReport = async (data, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.post("/reports", data);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const postReportDocumentOne = async (id, file, intl) => {
  try {
    if (await preRequest()) {
      const formData = new FormData();
      formData.append("file", file);
      const result = await axios.post(`/reports/${id}/documents`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const postReportDocumentMulti = async (id, file, intl) => {
  try {
    if (await preRequest()) {
      const formData = new FormData();
      for (let i = 0; i < file.length; i++) {
        formData.append(`files${i}`, file[i]);
      }
      const result = await axios.post(`/reports/${id}/documents/multi`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const deleteData = async (assets, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.delete(`/data?assets=${assets}`);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const deleteEquipment = async (id, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.delete(`/equipments/${id}`);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });

      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const postPoint = async (data, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.post("/points", data);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const putPoint = async (data, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.post("/points", data);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const putAsset = async (data, id, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.put(`/assets/${id}`, data);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const putEquipment = async (data, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.put(`/equipments/${data.id}`, data);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const deleteAsset = async (id, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.delete(`/assets/${id}`);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getAllAlertsData = async (data) => {
  try {
    if (await preRequest()) {
      const queryParams = {};
      if (!isNil(data) && !isNil(data.is_archived)) {
        queryParams.is_archived = data.is_archived;
      }
      const queryString = querystring.stringify(queryParams);

      const result = await axios.get(`/alerts/all?${queryString}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getAlertData = async (id) => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/alerts/${id}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const getAlertAdditionalInfo = async (id) => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/alerts/measurement/${id}/additional`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const getChunkInfo = async (id) => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/alerts/measurement/additional/${id}/data`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const putAlert = async (id, alert, type, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.put(`/alerts/${type}/${id}`, alert);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const deleteAlert = async (id, type) => {
  try {
    if (await preRequest()) {
      const result = await axios.delete(`/alerts/${type}/${id}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getAllUsersData = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/users");
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getUserData = async (id) => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/users/${id}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const postUser = async (user, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.post("/users", user);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const putUser = async (id, user, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.put(`/users/${id}`, user);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const deleteUser = async (id, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.delete(`/users/${id}`);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getConfigData = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get("/configurations");
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const putConfigData = async (data, id, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.put(`/configurations/${id}`, data);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getAnalysisSpatialData = async (
  asset = null,
  date = null,
  equipment_ID = null,
  channel = null,
  substructure = null,
  assetModule = null,
) => {
  try {
    if (await preRequest()) {
      const queryParams = {};
      if (!isNil(asset)) {
        queryParams.asset = asset;
      }
      if (!isNil(date)) {
        queryParams.dateTime = date;
      }
      if (!isNil(equipment_ID)) {
        queryParams.equipment_ID = equipment_ID;
      }
      if (!isNil(channel)) {
        queryParams.channel = channel;
      }
      if (!isNil(assetModule)) {
        queryParams.module = assetModule;
      }
      if (!isNil(substructure)) {
        queryParams.substructure = substructure.ref;
        if (!isNil(substructure.sections)) {
          queryParams.sections = substructure.sections;
        }
      }
      const queryString = querystring.stringify(queryParams);
      const result = await axios.get(`/data?${queryString}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const getAssetsDates = async (asset = null, equipment_ID = null, channel = null, substructure = null) => {
  try {
    if (await preRequest()) {
      const queryParams = {};
      if (!isNil(asset)) {
        queryParams.asset = asset.join(",");
      }
      queryParams.equipment_ID = equipment_ID;
      queryParams.channel = channel;
      queryParams.substructure = substructure;
      const queryString = querystring.stringify(queryParams);
      const result = await axios.get(`/data/dates?${queryString}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getAssetsDatetimes = async (
  asset = null,
  equipment_ID = null,
  channel = null,
  substructure = null,
  date = null,
  options = {}, // <-- AQUI aceptamos el signal
) => {
  try {
    if (await preRequest()) {
      const queryParams = {};
      if (!isNil(asset)) {
        queryParams.asset = asset.join(",");
      }
      queryParams.equipment_ID = equipment_ID;
      queryParams.channel = channel;
      queryParams.substructure = substructure;
      queryParams.date = date;

      const queryString = querystring.stringify(queryParams);

      // Pasamos el signal a Axios
      const result = await axios.get(`/data/times-by-date?${queryString}`, {
        signal: options.signal,
      });

      return result;
    }
  } catch (error) {
    if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
      return null; // NO lanzamos error
    }
  }
};

export const getAssetPointsSerialData = async (
  asset,
  startIndex,
  endIndex,
  endDate,
  fields,
  equipment,
  channel,
  substructure,
  user_period,
  maximum,
  startDate,
  afterAlert,
) => {
  try {
    if (await preRequest()) {
      const queryParams = { asset, equipment, channel, substructure, startIndex, endDate, fields };

      if (!isNil(substructure)) {
        queryParams.substructure = substructure.ref;
      }
      if (!isNil(startDate)) {
        queryParams.startDate = startDate;
      }

      if (!isNil(endIndex)) {
        queryParams.endIndex = endIndex;
      }
      if (user_period !== Number.POSITIVE_INFINITY && afterAlert) {
        queryParams.user_period = user_period;
      }
      if (maximum !== Number.POSITIVE_INFINITY) {
        queryParams.maximum = maximum;
      }
      const queryString = querystring.stringify(queryParams);
      const result = await axios.get(`/temporalData?${queryString}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getAssetPointsAdditionalData = async (
  asset,
  endDate,
  fields,
  equipment,
  user_period,
  maximum,
  channel,
  startDate,
  afterAlert,
  sensor_ID,
  startIndex,
  endIndex,
) => {
  try {
    if (await preRequest()) {
      const queryParams = { asset, endDate, fields, sensor_ID };
      if (!isNil(startDate)) {
        queryParams.startDate = startDate;
      }

      if (user_period !== Number.POSITIVE_INFINITY && afterAlert) {
        queryParams.user_period = user_period;
      }
      if (maximum !== Number.POSITIVE_INFINITY) {
        queryParams.maximum = maximum;
      }
      const queryString = querystring.stringify(queryParams);
      const result = await axios.get(`/data/additional/serial-time?${queryString}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getBooleanAnalysisSpatialData = async (asset = null, date = null) => {
  if (await preRequest()) {
    const queryParams = {};
    if (!isNil(asset)) {
      queryParams.asset = asset;
    }
    if (!isNil(date)) {
      queryParams.dateTime = date;
    }
    const queryString = querystring.stringify(queryParams);
    const result = await axios.get(`/accumulativesData?${queryString}`);
    return result;
  }
};

export const getMovingAverageData = async ({
  asset,
  equipment_ID,
  channel,
  substructure,
  date,
  movingAverage,
  module,
}) => {
  try {
    if (await preRequest()) {
      const queryParams = { asset, equipment_ID, channel, substructure, movingAverage, module };
      if (!isNil(asset)) {
        queryParams.asset = asset;
      }
      if (!isNil(date)) {
        queryParams.dateTime = date;
      }
      const queryString = querystring.stringify(queryParams);
      const result = await axios.get(`/data/moving_average?${queryString}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getMovingAverageTemporal = async ({
  asset,
  equipment,
  channel,
  substructure,
  date,
  startIndex,
  endIndex,
  endDate,
  movingAverage,
  movingAverageType,
  user_period,
  maximum,
  module,
}) => {
  try {
    if (await preRequest()) {
      const queryParams = {
        asset,
        equipment,
        channel,
        substructure,
        startIndex,
        endIndex,
        endDate,
        user_period,
        maximum,
        module,
      };
      queryParams.movingAverageValue = movingAverage;
      if (!isNil(asset)) {
        queryParams.asset = asset;
      }
      if (!isNil(date)) {
        queryParams.dateTime = date;
      }
      const queryString = querystring.stringify(queryParams);
      const result = await axios.get(`/temporalData/moving_average?${queryString}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

const preRequest = async () => {
  try {
    if (isTokenExpired()) {
      if (userWantToBeRemember()) {
        const refreshSuccess = await refreshTokenData();
        if (!refreshSuccess) {
          return false;
        }
      } else {
        return false;
      }
    }
    setAuthToken(axios, getFromCookie("uptechAccessToken"));
    return true;
  } catch (error) {
    logout();
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const putForgotPassword = async (email, intl, setLoading) => {
  try {
    if (await preRequest()) {
      const result = await axios.put(`/auth/forgotPassword?email=${email}`);

      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });

      setLoading(false);
      return result;
    }
  } catch (error) {
    toast.error(intl.formatMessage({ id: error.response.data.detail }), { className: "toast-error" });
  }
};

export const pingIpAddress = async (selectedEquipmentID, ipAddress, intl) => {
  try {
    if (await preRequest()) {
      const queryParams = {};
      if (!isNil(ipAddress)) {
        queryParams.ipAddress = ipAddress;
      }
      const queryString = querystring.stringify(queryParams);
      const result = await axios.get(`/equipments/${selectedEquipmentID}/ping?${queryString}`);

      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });

      return result;
    }
  } catch (error) {
    toast.error(intl.formatMessage({ id: error.response.data.detail }), { className: "toast-error" });
  }
};

export const getAlertConfigurations = async (id) => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/alerts-configurations`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const getAlertConfiguration = async (id) => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/alerts-configurations/${id}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const postAlertConfiguration = async (data, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.post("/alerts-configurations", data);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const putAlertConfiguration = async (data, id, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.put(`/alerts-configurations/${id}`, data);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const deleteAlertConfiguration = async (id, intl) => {
  try {
    if (await preRequest()) {
      const result = await axios.delete(`/alerts-configurations/${id}`);
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};

export const getShipTrackingHistorial = async (trackingId, shipId, startDate, endDate) => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/external-services/ships/tracking-historial`, {
        params: {
          tracking_id: trackingId,
          ship_id: shipId,
          //start_date: startDate,
          //end_date: endDate,
        },
      });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const simulateShip = async () => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/api/v1/simulate-custom-ship`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const getDoBData = async (data) => {
  try {
    //assets[], equipmentsIds[], substructures[], dateFrom, dateTo
    if (await preRequest()) {
      const queryParams = {};
      queryParams.assets = data.assets;
      queryParams.equipmentsIds = data.equipmentsIds;
      queryParams.substructures = data.substructures;
      const queryString = querystring.stringify(queryParams);

      const result = await axios.get(`/special-data?${queryString}`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const getDoBDataId = async (id) => {
  try {
    if (await preRequest()) {
      const result = await axios.get(`/special-data/${id}/file`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const postDoBData = async ({ data, file, intl }) => {
  try {
    if (await preRequest()) {
      //asset, equipmentId, channel, substructure, module, package, extraVar(al final no?), dateTime(solo fecha)
      const formData = new FormData();
      formData.append("special_data", JSON.stringify(data));
      formData.append("file", file);
      const result = await axios.post("/special-data", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(intl.formatMessage({ id: result.data.message }), { className: "toast-success" });
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
export const deleteDoBDataId = async (id) => {
  try {
    if (await preRequest()) {
      const result = await axios.delete(`/special-data/${id}/file`);
      return result;
    }
  } catch (error) {
    if (!isNil(error.message)) {
      toast.error(error.message, { className: "toast-error" });
    } else {
      throw Error(UNAUTHORIZED_USER);
    }
  }
};
